/**
 * API configuration and centralized request client.
 * - Uses VITE_API_URL for base URL.
 * - request() adds Authorization header and handles 401 (optional global handler).
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001';

export const API_BASE_URL = API_URL;
export const API_PATH = `${API_URL}/api`;

export const getToken = () =>
    localStorage.getItem('token') || sessionStorage.getItem('token');

let onUnauthorized = () => {};

/**
 * Set a global handler called when any request returns 401.
 * Typically used to clear auth state and redirect to login.
 */
export function setUnauthorizedHandler(fn) {
    onUnauthorized = typeof fn === 'function' ? fn : () => {};
}

/**
 * Fetch with auth header and optional 401 handling.
 * Use this for authenticated requests so 401 triggers logout/redirect in one place.
 *
 * @param {string} url - Full URL (e.g. API_PATH + '/events')
 * @param {RequestInit} [options] - fetch options (method, body, headers, etc.)
 * @returns {Promise<Response>} - fetch Response (check response.ok and await response.json() as needed)
 */
export async function request(url, options = {}) {
    const token = getToken();
    const headers = new Headers(options.headers);
    if (token) headers.set('Authorization', `Bearer ${token}`);

    const res = await fetch(url, { ...options, headers });

    if (res.status === 401) {
        onUnauthorized();
    }

    return res;
}

export default {
    API_BASE_URL,
    API_PATH,
    getToken,
    request,
    setUnauthorizedHandler,
};
